/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableModel} from '../../shared/table/models/table.model';
import {PeriodsTableColumnEnum} from './models/periods-table-column.enum';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import periodsTableHeadersConfiguration from './configuration/periods-table-headers-configuration';
import {PeriodSummaryModel} from '../models/period/period-summary/period-summary.model';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {
    Subscription,
    timer
} from 'rxjs';
import {BundlesApi} from '../api/bundles.api';
import {PeriodsTableSearchCriteriaService} from './services/periods-table-search-criteria.service';
import {PeriodSearchCriteriaModel} from '../models/period/period-search-criteria/period-search-criteria.model';
import {finalize} from 'rxjs/operators';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PeriodsTableStateModel} from './models/periods-table.state.model';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import moment from 'moment';
import {PeriodDetailModel} from '../models/period/period-detail/period-detail.model';
import {PeriodsTypeEnum} from './models/periods-type.enum';
import {ReferenceDataModel} from '../../api/reference-data.model';
import {NgbCalendar} from '@ng-bootstrap/ng-bootstrap';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {DatepickerHelperService} from '../../shared/utils/datepicker-helper/datepicker-helper.service';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {InputDateComponent} from '../../shared/input/input-date/input-date.component';
import {BundlesConfigService} from '../services/bundles-config.service';
import {
    AbstractControl,
    ValidatorFn
} from '@angular/forms';
import {WarningsEnum} from '../../shared/warnings/warnings.enum';
import {InputMessagesService} from '../../shared/input/service/input-messages.service';
import {objectsValidator} from '../../shared/input/input-validators';
import {UserService} from '../../shared/services/user.service';
import {Permission} from '../../shared/utils/permission.enum';
import {PeriodBundlesStatusDisplayModel} from './models/period-bundles-status-display.model';
import {PeriodBundlesStatusEnum} from './models/period-bundles-status.enum';
import {datePipe} from '../util/bundle-data-pipes';
import {DateModel} from '../models/date/date.model';
import {UserBaseModel} from '../models/user-base/user-base.model';
import {
    openLine,
    scrollIntoView
} from '../util/info-charts/info-chart';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {isDeepStrictEqual} from 'util';

@Component({
    selector: 'app-periods-table',
    templateUrl: './periods-table.component.html',
    styleUrls: ['./periods-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PeriodsTableComponent extends StatefulComponent<PeriodsTableStateModel> implements OnDestroy, OnChanges, AfterViewInit {
    @Input() searchCriteria: PeriodSearchCriteriaModel = {} as PeriodSearchCriteriaModel;
    @Input() periodsList: PeriodDetailModel[];

    @Output() refreshTableData = new EventEmitter<void>();
    @Output() refreshPeriodData = new EventEmitter<PeriodDetailModel>();
    @Output() mouseHoverLineEmitter = new EventEmitter<string>();

    // Template for the period type table cell
    @ViewChild('periodTypeTemplate') periodTypeTemplate: TemplateRef<ElementRef>;
    // Template for the time period table cell
    @ViewChild('timePeriodTemplate') timePeriodTemplate: TemplateRef<ElementRef>;
    // Template for the Bundles status table cell
    @ViewChild('bundlesStatusTemplate') bundlesStatusTemplate: TemplateRef<ElementRef>;
    @ViewChild('editInputDate', {static: true}) editInputDate: InputDateComponent;
    public requestedDateInputMessages = new Map<WarningsEnum, string[]>();
    public infoChartData: PeriodSummaryModel[];
    // Table name
    public readonly TABLE_NAME = 'period list';
    // Full line tooltip detail
    private readonly FULL_LINE_TOOLTIP_DETAIL: string = 'period line';
    // Color on change end date values
    private readonly DEFAULT_COLOR_TEXT = '#444444';
    private readonly ANIMATION_COLOR_TEXT = '#3DB014';
    // Animation timeout
    private readonly ANIMATION_TIMEOUT = 5000;
    readonly fontSizeDot = 10;
    readonly minFontsizeDot = 7;
    // Icon user info
    public readonly PERIOD_CREATED_BY_LABEL = 'Last created/ changed by:';
    public readonly PERIOD_CREATED_ON_LABEL = 'Created/changed on:';

    public tableData: TableModel<PeriodsTableColumnEnum, PeriodDetailModel> = {
        headers: {
            entries: new Map<PeriodsTableColumnEnum, TableHeaderEntryModel<PeriodsTableColumnEnum>>([
                [PeriodsTableColumnEnum.PERIOD_TYPE, periodsTableHeadersConfiguration.periodType],
                [PeriodsTableColumnEnum.TIME_PERIOD, periodsTableHeadersConfiguration.timePeriod],
                [PeriodsTableColumnEnum.BUNDLES_STATUS, periodsTableHeadersConfiguration.bundlesStatus]
            ])
        },
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true
    };
    // Local Loading Key
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'PERIOD_LIST_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    /** Inputs validators */
    validators: { [key: string]: ValidatorFn[] } = {
        requestedDate: [objectsValidator]
    };
    // Period Bundles Status labels to display in the table
    periodBundlesStatusLabels = new Map<PeriodBundlesStatusEnum, string>([
        [PeriodBundlesStatusEnum.OPEN, 'Open'],
        [PeriodBundlesStatusEnum.CLOSED, 'Closed'],
        [PeriodBundlesStatusEnum.TOTAL, 'Total']
    ]);

    private subscriptions = new Subscription();

    constructor(public spinnerService: SpinnerService,
                public search: PeriodsTableSearchCriteriaService,
                private cdr: ChangeDetectorRef,
                private bundlesApi: BundlesApi,
                private calendar: NgbCalendar,
                private toast: ToastWrapperService,
                private bundlesConfigService: BundlesConfigService,
                private datepickerHelperService: DatepickerHelperService,
                private inputMessagesService: InputMessagesService,
                private userService: UserService,
                private periodsViewsService: PeriodsViewsService) {
        super();
    }

    ngAfterViewInit(): void {
        this.handleRetrievedData();
        // If the page is changed we must restore the selected line to default in order to updated the table data
        this.subscriptions.add(
                this.search.changePageEvent.subscribe(() => this.storeState({selectedLine: null}))
        );
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['periodsList'].firstChange && changes['periodsList'].currentValue) {
            if (this.state.selectedLine && this.periodsViewsService.updateTableLinesByGlobalSearchButton) {
                this.periodsViewsService.updateTableLinesByGlobalSearchButton = false;
                this.handleRetrievedData();
            } else if (!this.state.selectedLine || this.state?.animateDates) {
                this.handleRetrievedData();
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): PeriodDetailModel | null {
        let accordionData: PeriodDetailModel | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    get hasBundlePeriodManagePermission(): boolean {
        return !this.userService.hasPermissions([Permission.ADMIN_BUNDLES_PERIOD_MANAGE]);
    }

    get periodsListTitle(): string {
        const periodsCount = this.tableData?.staticSection?.length > 0 ? this.tableData.staticSection.length : 0;
        return `Periods list (${periodsCount})`;
    }

    get openSelectedLineIdentifier(): string {
        return this.state?.selectedLine?.isOpen ? this.state?.selectedLine.identifier : null;
    }

    periodSentOn(periodChangedOn: DateModel): string {
        return datePipe(periodChangedOn);
    }

    setMinDate(periodStartDate: number, periodType: string): NgbDate {
        let finalDate: NgbDate;
        if (periodType === PeriodsTypeEnum.PRESENT) {
            finalDate = this.datepickerHelperService.todayDate(this.bundlesConfigService.timezone);
        } else {
            finalDate = this.datepickerHelperService
                    .convertTimestampTimeZoneToNgbDate(this.bundlesConfigService.timezone, periodStartDate);
        }
        return finalDate;
    }

    clickOnLineComponent(event: Event): void {
        event.stopPropagation();
    }

    animationInputDateStyle(code: string, isViewMode?: boolean): { [key: string]: any } {
        let changeValue;
        if (isViewMode) {
            changeValue = this.state?.changeEndDateValues?.get(code)?.startDate.haveAnimation;
        } else {
            changeValue = this.state?.changeEndDateValues?.get(code)?.endDate.haveAnimation;
        }
        const colorAnimation = changeValue ? this.ANIMATION_COLOR_TEXT : this.DEFAULT_COLOR_TEXT;
        return {
            'transition': `color 1s linear`,
            'color': colorAnimation
        };
    }

    openLine(line: string, isFromChart?: boolean, hasScroll?: boolean): void {
        const retrievedLine = openLine<PeriodsTableColumnEnum, PeriodDetailModel>(
                this.tableData,
                line,
                isFromChart);
        if (retrievedLine) {
            retrievedLine.accordionData = this.state.periodsData?.find((period) => period.code === retrievedLine?.identifier);

            if (hasScroll) {
                scrollIntoView(retrievedLine.identifier);
            }
        }
        this.storeState({selectedLine: retrievedLine});
    }

    mouseHoverLine(identifier: string): void {
        this.tableData.staticSection.map((item) => item.isMouseHover = false);
        const line = this.tableData.staticSection.find((item) => item.identifier === identifier);
        if (line != null) {
            line.isMouseHover = !line.isMouseHover;
        }
    }

    scrollIntoView(identifier: string): void {
        const elementToScroll = document.getElementById(`${identifier}`);
        elementToScroll?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    manageRequestedDateInputStateChange(date: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(date,
                this.requestedDateInputMessages, 'error');
        this.requestedDateInputMessages = new Map(this.requestedDateInputMessages);
    }

    /**
     * Returns the respective css font size for ones, tens and hundreds
     * @param count count of bundles status
     * @returns
     */
    getBundlesStatusFontSize(count: number): { [key: string]: string } {
        let fontSize = this.fontSizeDot;
        // the font size changes form hundreds
        if (count >= 100) {
            fontSize = this.minFontsizeDot;
        }
        return {'font-size': `${fontSize}px`};
    }

    public convertTimestampToDate(timestamp: number, timeZone?: string): string {
        let date = null;
        if (timestamp != null) {
            date = timeZone != null ? moment(timestamp)
                    .tz(timeZone)
                    .format('DD/MM/YYYY') : moment(timestamp)
                    .format('DD/MM/YYYY');
        }
        return date;
    }

    public isDisabledCalendarButton(startDate: number): boolean {
        return moment()
                .tz(this.bundlesConfigService.timezone)
                ?.valueOf() < startDate;
    }

    updatePeriodValues(periodDetail: PeriodDetailModel): void {
        if (periodDetail != null) {
            const line = this.tableData.staticSection.find((item) => item.identifier === periodDetail.code);
            if (line != null) {
                line.accordionData = periodDetail;
                const lineSummary = this.getPeriodSummaryFromDetail(line.accordionData);
                line.data = this.mapLineData(lineSummary, this.getLineIndex(line.identifier));
                this.storeState({selectedLine: line});

                // emit event to refresh the period detail data on the stream
                this.refreshPeriodData.emit(periodDetail);
            }
        }
    }

    isFlyingMenuVisible(periodChangedOn: DateModel, periodChangedBy: UserBaseModel): Boolean {
        return this.userService.hasPermissions(
                [
                    Permission.ADMIN_BUNDLES_PERIOD_VIEW,
                    Permission.CUSTOMER_BUNDLES_PERIOD_VIEW
                ]) && periodChangedOn != null && periodChangedBy != null;
    }

    managePeriodTypeDotLineTop(periodTypeIndex: number): boolean {
        // Not displayed on first result of the first page
        if (periodTypeIndex == null || (periodTypeIndex === 0 && this.state.currentPage === 1)) {
            return false;
        }
        return true;
    }

    managePeriodTypeDotLineBottom(periodTypeIndex: number): boolean {
        const lastPageElementIndex = this.search.totalResults - 1;
        let actualPeriodTypeIndex = periodTypeIndex;
        if (this.state.currentPage > 1) {
            actualPeriodTypeIndex = (this.state.currentPage - 1) * this.search.pageSize + periodTypeIndex;
        }

        // Not displayed on the last result of the last page
        if (periodTypeIndex == null || actualPeriodTypeIndex === lastPageElementIndex) {
            return false;
        }
        return true;
    }

    isLineOpen(periodTypeIndex: number): boolean {
        return (this.tableData?.staticSection ?? false) && (this.tableData?.staticSection[periodTypeIndex]?.isOpen ?? false);
    }

    isLineOpenAndHover(periodTypeIndex: number): boolean {
        return this.isLineOpen(periodTypeIndex) && (this.state.isMouseHoverOpenLine ?? false);
    }

    mouseHoverLineHandler(isHover: boolean): void {
        if (isHover !== this.state.isMouseHoverOpenLine) {
            this.storeState({isMouseHoverOpenLine: isHover});
        }
    }

    mouseHoverHandler(tableLine: TableLineModel<PeriodsTableColumnEnum, PeriodDetailModel>): void {
        this.storeState({isMouseHoverLine: tableLine?.identifier});
    }

    getLastChangedByMouseHoverLine(): UserBaseModel {
        const tooltipLastChangedBy = this.state?.periodsData.find((item) => item.code === this.state.isMouseHoverLine)?.lastChangedBy;
        if (JSON.stringify(this.state.tooltipLastChangedBy) !== JSON.stringify(tooltipLastChangedBy) && tooltipLastChangedBy != null ){
            this.storeState({tooltipLastChangedBy});
        }
        return this.state?.tooltipLastChangedBy;

    }

    getLastChangedOnMouseHoverLine(): DateModel {
        const tooltipLastChangedOn = this.state?.periodsData.find((item) => item.code === this.state.isMouseHoverLine)?.lastChangedOn;
        if (JSON.stringify(this.state.tooltipLastChangedOn) !== JSON.stringify(tooltipLastChangedOn) && tooltipLastChangedOn != null ){
            this.storeState({tooltipLastChangedOn});
        }
        return this.state?.tooltipLastChangedOn;
    }

    private getLineIndex(lineIdentifier: string): number {
        return this.tableData?.staticSection?.findIndex((item) => item.identifier === lineIdentifier);
    }

    private getPeriodSummaryFromDetail(lineData: PeriodDetailModel): PeriodSummaryModel {
        return {
            code: lineData.code,
            timePeriod: lineData.timePeriod,
            periodType: lineData.periodType,
            periodBundlesStatus: lineData.periodBundlesStatus,
            lastChangedBy: lineData?.lastChangedBy,
            lastChangedOn: lineData?.lastChangedOn,
            totalAmount: lineData.totalAmount
        };
    }

    private mapDynamicEndDatesValues(values: PeriodDetailModel[], enabledAnimation?: boolean): void {
        const changedEndDateValues: Map<string, any> = new Map<string, any>();

        values?.forEach((item) => {
            if (<PeriodsTypeEnum>item?.periodType?.code !== PeriodsTypeEnum.PAST) {
                const endDate = this.state.changeEndDateValues?.get(item.code)?.endDate.value;
                const startDate = this.state.changeEndDateValues?.get(item.code)?.startDate.value;

                if (endDate != null && enabledAnimation) {
                    if (endDate !== item.timePeriod.endDate && startDate !== item.timePeriod.startDate) {
                        changedEndDateValues.set(item.code, {
                            endDate: {
                                value: item.timePeriod.endDate,
                                haveAnimation: true
                            },
                            startDate: {
                                value: item.timePeriod.startDate,
                                haveAnimation: true
                            }
                        });
                    } else if (endDate !== item.timePeriod.endDate && startDate === item.timePeriod.startDate) {
                        changedEndDateValues.set(item.code, {
                            endDate: {
                                value: item.timePeriod.endDate,
                                haveAnimation: true
                            },
                            startDate: {
                                value: item.timePeriod.startDate,
                                haveAnimation: false
                            }
                        });
                    } else if (endDate === item.timePeriod.endDate && startDate !== item.timePeriod.startDate) {
                        changedEndDateValues.set(item.code, {
                            endDate: {
                                value: item.timePeriod.endDate,
                                haveAnimation: false
                            },
                            startDate: {
                                value: item.timePeriod.startDate,
                                haveAnimation: true
                            }
                        });
                    }
                } else {
                    changedEndDateValues.set(item.code, {
                        endDate: {
                            value: item.timePeriod.endDate,
                            haveAnimation: false
                        },
                        startDate: {
                            value: item.timePeriod.startDate,
                            haveAnimation: false
                        }
                    });
                }
            }
        });
        this.storeState({changeEndDateValues: changedEndDateValues});
    }

    /**
     * Populates the staticData array with the current {@link PeriodDetailModel}
     * @param periodTableData the {@link PeriodDetailModel[]} from which to update the array
     */
    private mapData(periodTableData: PeriodDetailModel[]): TableLineModel<PeriodsTableColumnEnum, PeriodDetailModel>[] {
        let lines: TableLineModel<PeriodsTableColumnEnum, PeriodDetailModel>[] = [];
        if (periodTableData != null) {
            lines = periodTableData.map((line: PeriodDetailModel, index: number) => ({
                isOpen: false,
                identifier: line.code,
                data: this.mapLineData(line, index)
            } as TableLineModel<PeriodsTableColumnEnum, PeriodDetailModel>));
        }
        return lines;
    }

    /**
     * Maps a {@link PeriodSummaryModel} to a Map<PeriodsTableColumnEnum, TableLineDataModel>
     */
    private mapLineData(data: PeriodSummaryModel, index: number): Map<PeriodsTableColumnEnum, TableLineDataModel> {
        const map: Map<PeriodsTableColumnEnum, TableLineDataModel> = new Map<PeriodsTableColumnEnum, TableLineDataModel>([
            [
                PeriodsTableColumnEnum.PERIOD_TYPE, {
                cellValue: {
                    templateRef: this.periodTypeTemplate,
                    context: {
                        periodType: data.periodType,
                        periodTypeIndex: index
                    }
                }
            }
            ],
            [
                PeriodsTableColumnEnum.TIME_PERIOD, {
                cellValue: {
                    templateRef: this.timePeriodTemplate,
                    context: {
                        showInputDate: this.showEditInputDate(data.periodType),
                        timePeriodCode: data.code,
                        timePeriodStartDate: data.timePeriod?.startDate,
                        timePeriodEndDate: data.timePeriod?.endDate,
                        periodType: data.periodType?.code,
                        periodChangedBy: data?.lastChangedBy,
                        periodChangedOn: data?.lastChangedOn
                    }
                }
            }
            ],
            [
                PeriodsTableColumnEnum.BUNDLES_STATUS, {
                cellValue: {
                    templateRef: this.bundlesStatusTemplate,
                    context: {
                        bundlesStatusData: [
                            this.getBundleStatusDisplayData(data?.periodBundlesStatus?.open, PeriodBundlesStatusEnum.OPEN),
                            this.getBundleStatusDisplayData(data?.periodBundlesStatus?.closed, PeriodBundlesStatusEnum.CLOSED),
                            this.getBundleStatusDisplayData(data?.periodBundlesStatus?.total, PeriodBundlesStatusEnum.TOTAL)
                        ]
                    }
                }
            }
            ]
        ]);
        return map;
    }

    private getBundleStatusDisplayData(count: number, type: PeriodBundlesStatusEnum): PeriodBundlesStatusDisplayModel {
        return {
            count: count != null ? count : 0,
            type: type,
            label: this.periodBundlesStatusLabels.get(type)
        };
    }

    private showEditInputDate(periodType: ReferenceDataModel): boolean {
        return <PeriodsTypeEnum>periodType?.code !== PeriodsTypeEnum.PAST;
    }

    private changePeriodTime(periodCode: string, endDate: NgbDate): void {
        const endDateValue = this.datepickerHelperService
                .convertNgbDateToTimestampTimeZoneEndDate(endDate, this.bundlesConfigService.timezone);

        if (endDateValue != null && endDateValue > 0) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            this.bundlesApi.updateEndDate(periodCode, {endDate: endDateValue})
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                    }))
                    .subscribe(
                            () => {
                                this.toast.success(`The period end date was successfully changed!`, 'Success');
                                this.storeState({animateDates: true});
                                this.refreshTableData.emit();
                            },
                            (() => {
                                this.refreshTableData.emit();
                            }));
        }
    }

    /**
     * Handles retrieved period list data from the BE service
     */
    private handleRetrievedData(): void {
        const hasAnimation = this.state?.animateDates;

        this.storeState({
            currentPage: this.search.pageNumber,
            periodsData: this.periodsList
        });

        this.mapDynamicEndDatesValues(this.state.periodsData, hasAnimation);
        this.tableData.staticSection = this.mapData(this.state.periodsData);
        this.tableData.noFilterResultsFoundTemplate = this.periodsViewsService.isGlobalSearchCriteriaApplied();
        this.infoChartData = this.state.periodsData?.length ? this.state.periodsData : null;

        timer(this.ANIMATION_TIMEOUT)
                .subscribe(() => {
                    this.mapDynamicEndDatesValues(this.state.periodsData, false);
                    this.storeState({animateDates: false});
                });
    }
}
